import PropTypes from "prop-types";
import { DEFAULT_PROFILE_AVATAR } from "@/config/default_avatar";

const ProfileAvatar = ({
  imageUrl,
  badgeNumber,
  completionPercentage = 0,
  size = "medium",
  className = "",
  badgePosition = "top-right",
  altText = "Profile",
}) => {
  const sizeConfigs = {
    small: {
      container: "w-[80px] h-[80px]",
      image: "w-[65px] h-[65px]",
      badge: "w-6 h-6 text-xs",
    },
    medium: {
      container: "w-[120px] h-[120px]",
      image: "w-[100px] h-[100px]",
      badge: "w-8 h-8 text-base",
    },
    large: {
      container: "w-[160px] h-[160px]",
      image: "w-[135px] h-[135px]",
      badge: "w-10 h-10 text-lg",
    },
  };

  const badgePositions = {
    "top-right": "top-8 -right-3",
    "top-left": "top-8 -left-3",
    "bottom-right": "bottom-8 -right-3",
    "bottom-left": "bottom-8 -left-3",
  };

  const config = sizeConfigs[size] || sizeConfigs.medium;
  const badgePos = badgePositions[badgePosition] || badgePositions["top-right"];

  return (
    <div className={`relative ${config.container} ${className}`}>
      <svg
        className="absolute top-0 left-0 w-full h-full"
        viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke="#E5E5E5"
          strokeWidth="2"
        />
        {/* Full circle so 100% completion can close the ring (old path was only ~3/4). */}
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray="100 100"
          strokeDashoffset={
            100 - Math.min(100, Math.max(0, completionPercentage))
          }
          transform="rotate(-90 50 50)"
        />
      </svg>

      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${config.image} rounded-full overflow-hidden`}>
        <img
          src={imageUrl || DEFAULT_PROFILE_AVATAR}
          alt={altText}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = DEFAULT_PROFILE_AVATAR;
          }}
        />
      </div>

      {badgeNumber !== undefined && badgeNumber !== null && (
        <div
          className={`absolute ${badgePos} ${config.badge} bg-primary rounded-full text-white flex items-center justify-center z-10`}>
          {badgeNumber}
        </div>
      )}
    </div>
  );
};

ProfileAvatar.propTypes = {
  imageUrl: PropTypes.string,
  badgeNumber: PropTypes.number,
  completionPercentage: PropTypes.number,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  className: PropTypes.string,
  badgePosition: PropTypes.oneOf([
    "top-right",
    "top-left",
    "bottom-right",
    "bottom-left",
  ]),
  altText: PropTypes.string,
};

export default ProfileAvatar;
